/**
 * PromptPay QR Code payload generator
 *
 * ตาม EMVCo QR Code specification ที่ BoT (ธนาคารแห่งประเทศไทย) กำหนด
 * ทำงานบน Cloudflare Workers ได้ (ไม่มี dependency ภายนอก)
 *
 * อ้างอิง:
 *   - https://www.bot.or.th/Thai/PaymentSystems/StandardPS/Documents/ThaiQRCode_Payment_Spec_v1_p2.pdf
 *   - https://github.com/dtinth/promptpay-qr
 */

// ─── EMVCo TLV helpers ────────────────────────────────────────────────────────

/**
 * สร้าง EMVCo TLV (Tag-Length-Value) field
 * @param id    - tag ID (2 digits)
 * @param value - string value
 */
function tlv(id: string, value: string): string {
  const len = value.length.toString().padStart(2, "0");
  return `${id}${len}${value}`;
}

// ─── CRC-16/CCITT-FALSE ───────────────────────────────────────────────────────

/**
 * คำนวณ CRC-16/CCITT-FALSE checksum
 * polynomial: 0x1021, init: 0xFFFF, no input/output reflection
 */
function crc16(data: string): string {
  let crc = 0xffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = (crc << 1) ^ 0x1021;
      } else {
        crc = crc << 1;
      }
      crc &= 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, "0");
}

// ─── PromptPay ID normalizer ──────────────────────────────────────────────────

export type PromptPayType = "phone" | "national_id" | "tax_id" | "ewallet";

/**
 * Normalize PromptPay ID ตาม spec
 * - Phone: 0812345678 → 0066812345678 (แทน 0 นำหน้าด้วย 0066)
 * - National ID: 1234567890123 (13 หลัก ไม่แปลง)
 * - Tax ID: เหมือน National ID
 * - eWallet: ไม่แปลง
 */
function normalizePromptPayId(id: string, type: PromptPayType): string {
  const cleaned = id.replace(/[-\s]/g, "");
  if (type === "phone") {
    if (cleaned.startsWith("0") && cleaned.length === 10) {
      return "0066" + cleaned.slice(1);
    }
    if (cleaned.startsWith("+66")) {
      return "00" + cleaned.slice(1);
    }
    if (cleaned.startsWith("66") && cleaned.length === 11) {
      return "00" + cleaned;
    }
    return cleaned;
  }
  return cleaned;
}

// ─── Merchant Account sub-IDs ─────────────────────────────────────────────────
// AID (Application ID) แตกต่างกันตามประเภท PromptPay
const PROMPTPAY_AID: Record<PromptPayType, string> = {
  phone:       "A000000677010111",
  national_id: "A000000677010111",
  tax_id:      "A000000677010111",
  ewallet:     "A000000677010114",
};

// Tag สำหรับ Merchant Identifier ภายใน sub-record 29
const PROMPTPAY_TAG: Record<PromptPayType, string> = {
  phone:       "01", // Mobile
  national_id: "02", // National ID
  tax_id:      "02", // Tax ID ใช้ tag เดียวกัน
  ewallet:     "03", // eWallet
};

// ─── Public API ──────────────────────────────────────────────────────────────

export interface PromptPayOptions {
  /** PromptPay ID (เบอร์โทร / เลขบัตร / เลขนิติบุคคล / eWallet ID) */
  id: string;
  /** ประเภท ID default: "phone" */
  type?: PromptPayType;
  /** จำนวนเงิน (บาท) — ถ้าไม่ระบุจะเป็น dynamic amount */
  amount?: number;
}

/**
 * Generate PromptPay QR payload string (EMVCo format)
 * ส่งไปยัง qrcode library เพื่อ generate QR image
 *
 * @example
 * generatePromptPayPayload({ id: "0812345678", amount: 500 })
 * // → "000201010212..."
 */
export function generatePromptPayPayload(options: PromptPayOptions): string {
  const { id, type = "phone", amount } = options;

  const normalizedId = normalizePromptPayId(id, type);
  const aid = PROMPTPAY_AID[type];
  const tag = PROMPTPAY_TAG[type];

  // ─── Build payload (ไม่มี CRC ตอนนี้ — ใส่ CRC placeholder ท้ายสุด)
  // ลำดับตาม EMVCo + BoT spec ที่ promptpay.io ใช้:
  //   00 → 01 → 29 → 58 → 53 → 54 (optional) → 63

  let payload = "";

  // 00: Payload Format Indicator = 01
  payload += tlv("00", "01");

  // 01: Point of Initiation Method
  //   11 = Static (ไม่มีจำนวนเงิน), 12 = Dynamic (มีจำนวนเงิน)
  payload += tlv("01", amount != null ? "12" : "11");

  // 29: Merchant Account Information (PromptPay)
  const merchantInfo = tlv("00", aid) + tlv(tag, normalizedId);
  payload += tlv("29", merchantInfo);

  // 58: Country Code = TH (ก่อน 53 ตาม reference spec)
  payload += tlv("58", "TH");

  // 53: Transaction Currency = 764 (THB)
  payload += tlv("53", "764");

  // 54: Transaction Amount (optional)
  if (amount != null && amount > 0) {
    // format: ไม่มีทศนิยมถ้าเป็นจำนวนเต็ม, มีทศนิยมเฉพาะเมื่อจำเป็น
    // ตัวอย่าง: 100 → "100", 99.5 → "99.50", 900 → "900"
    const amountStr =
      amount % 1 === 0
        ? String(Math.round(amount))
        : amount.toFixed(2);
    payload += tlv("54", amountStr);
  }

  // 63: CRC — length always 04, value = CRC ของทุกอย่างข้างต้น + "6304"
  const crcInput = payload + "6304";
  payload += "6304" + crc16(crcInput);

  return payload;
}
